<template>
	<AppCard class="card-margin" :title="getString('x_service_settings', [userStore.deliveryWord])" :button-text="getString('save')" @button-click="saveSettings">
		<template v-slot:body>
			<AppList :items="statusItems">
				<template #number-input-slot="{item}">
					<div class="row centered space-between">
						<p class="number-p">{{item.title}}</p>
						<AppNumberInput class="input-item" :min="0" :rightSideSymbol="item.rightSymbol" :symbol="item.symbol || ''" :value="item.value" @changed-value="v=>{item.valueCallback(v)}" />
					</div>
				</template>
				<template #amount-input-slot="{item}">
					<div class="row centered space-between">
						<p class="number-p">{{item.title}}</p>
						<AppNumberInput doubleDecimal class="input-item" :min="0" :rightSideSymbol="item.rightSymbol" :symbol="item.symbol || ''" :value="currencyStore.dbToActualValue(item.value)" @changed-value="v=>{item.valueCallback(v)}" />
					</div>
				</template>
				<template #future-days="{item}">
					<div class="row centered space-between">
						<p class="number-p">{{ getString('scheduled_days_advance') }}</p>
						<AppNumberInput class="input-item" :min="0" :value="item.value" @changed-value="v=>{item.valueCallback(v)}" />
					</div>
				</template>
				<template #timetables="{item}">
					<div class="row centered space-between">
						<p class="button-p">{{item.title}}</p>
						<AppButton class="input-item" small @click="item.callback" outline :text="getString('set_hours')"/>
					</div>
				</template>
				<template #courier-options="{item}">
					<div class="dropdown-list">
						<AppList :items="item.itemChildren" >
							<template #checklist="{item}">
								<div class="checklist-container">
									<p class="switch-p">{{item.title}}</p>
									<AppChecklist :title="item.title" :values="item.couriers" :selectedIndexes="item.selected" @change-checked="item.callback" />
								</div>
							</template>
							<template #number-input-slot="{item}">
								<div class="row centered space-between">
									<p class="number-p">{{ item.titleNumber }}</p>
									<AppNumberInput class="input-item" :min="item.min ? item.min : 0" :rightSideSymbol="item.rightSymbol" :symbol="item.symbol || ''" :value="item.value" @changed-value="v=>{item.valueCallback(v)}" />
								</div>
							</template>
						</AppList>
					</div>
				</template>
			</AppList>
		</template>
	</AppCard>
	<AppBottomSheet :force-width="bottomSheetWidth" :active="timetablesSheetActive" @toggling-popup="closeTimetablesSheet">
		<div class="timetables-content" v-if="timetablesSheetActive">
            <div class="row centered">
                <AppLoader v-if="loadingTimetables" primary />
            </div>
            <TimetablesEditor delivery v-if="!loadingTimetables && loadedTimetables" :title="getString('x_timetables', [userStore.deliveryWord])" with-quantity with-intervals :timetables="timetables" @save-clicked="closeTimetablesSheet" />
        </div>
	</AppBottomSheet>
	<AppPopup ref="popup-other-payments" :title="getString('enable_other_payments_methods')" :buttons="loadingOtherPayments ? [] : [{text: getString('cancel'), negative: true, callback: cancelSubOtherPayments}, {text: getString('continue'), callback: subOtherPaymentsMethod}]">
		<div v-if="loadingOtherPayments" class="row centered loader-container"><AppLoader primary /></div>
		<p v-if="!loadingOtherPayments" class="popup-body-text">{{ getString('enable_other_payments_methods_explain') }}</p>
	</AppPopup>
	<CheckoutInPlace :client-secret="clientSecret" @toggling-popup="togglingPopupSheet" @checkout-complete="paymentComplete" />
</template>

<script>
import AppCard from "@/shared/components/AppCard.vue";
import AppList from "@/shared/components/AppList.vue";
import AppPopup from "@/shared/components/AppPopup.vue";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppSwitch from "@/shared/components/AppSwitch.vue";
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import TimetablesEditor from "@/console-new/components/shared/TimetablesEditor.vue";
import AppChecklist from "@/shared/components/AppChecklist.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";

import {useCurrencyStore, useLanguageStore, useNotificationStore} from "@/shared/stores";
import {useUserStore} from "@/console-new/stores";
import CheckoutInPlace from "@/console-new/components/shared/CheckoutInPlace.vue";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let userStore = useUserStore();
		const notificationStore = useNotificationStore();
		const currencyStore = useCurrencyStore();
		return {getString: languageStore.getString, showNotification: notificationStore.showNotification, currencyStore, userStore}
	},
	name: "DeliverySettingsCard",
	mixins: [WindowWidth],
	emits: ['save-settings'],
	components: {
		CheckoutInPlace,
		AppCard, AppList, AppNumberInput, AppPopup, AppBottomSheet, AppLoader, TimetablesEditor, AppButton, AppSwitch, AppChevronIcon, AppChecklist},
	data() {
		return {
			editedSettings: false,
			timetablesSheetActive: false,
			loadingTimetables: false,
			loadedTimetables: false,
			timetables: [],
			loadingOtherPayments: false,
			couriersIdsIndexes: {
				3851: 0,
				993: 1,
			},
			clientSecret: null,
			lastFreeDeliveryValue: null,
		}
	},
	computed: {
		selectedCouriers() {
			let couriers = this.userStore.deliveryCouriers;
			if(!couriers) return [];
			return couriers.map(courier => this.couriersIdsIndexes.hasOwnProperty(Number(courier.courier_id)) ? this.couriersIdsIndexes[Number(courier.courier_id)] : null).filter(id => id !== null);
		},
		timetablesTitle() {
			return this.userStore.currBusiness && this.userStore.currBusiness.type === 'other' ? this.getString('x_timetables', [this.userStore.deliveryWord]) : this.getString('delivery_timetables');
		},
		freeDeliveryValue() {
			if(!this.userStore.currBusiness) {
				return null;
			}
			const dbValue = this.userStore.currBusiness.delivery_courier_free_shipping;
			if(dbValue === null || dbValue === 0) {
				return this.lastFreeDeliveryValue || this.currencyStore.dbToActualValue(this.currencyStore.currCurrency.min_free_delivery_amount);
			}
			return this.currencyStore.dbToActualValue(dbValue);
		},
		statusItems() {
			let items = [
				{
					title: this.timetablesTitle,
					customSlot: 'timetables',
					callback: this.openTimetables
				}, {
					title: this.getString('cash_payment'),
					switch: true,
					active: this.deliveryCash,
					callback: this.changeDeliveryCash,
				}
			];

			if(this.userStore.currBusiness && this.userStore.currBusiness.country === "IT") {
				items.push({
					title: this.getString('courier_delivery'),
					switch: true,
					active: this.deliveryCourier,
					callback: (newStatus) => {
						this.editedSettings = true;
						this.userStore.currBusiness.delivery_courier = newStatus;
					}, dropdownSlot: 'courier-options', itemChildren: [
						{title: this.getString("select_couriers"), selected: this.selectedCouriers, customSlot: "checklist", couriers: [
								"DHL", "Poste Italiane"
							], callback: (indexes) => {
								let courierIds = {};
								Object.entries(this.couriersIdsIndexes).forEach(([key, value]) => courierIds[value] = key);
								this.userStore.deliveryCouriers = indexes.map(index => ({courier_id: courierIds[index]}));
								this.editedSettings = true;
							}},
						{
							title: this.getString("free_delivery"),
							titleNumber: this.getString("free_delivery_amount"),
							switch: true,
							active: this.userStore.currBusiness && this.userStore.currBusiness.delivery_courier_free_shipping,
							symbol: this.currencyStore.currCurrency.symbol,
							min: this.currencyStore.dbToActualValue(this.currencyStore.currCurrency.min_free_delivery_amount),
							value: this.freeDeliveryValue,
							explain: this.getString("free_delivery_explain"),

							dropdownSlot: 'number-input-slot',
							callback: (active) => {
								if(active) {
									const minValue = this.currencyStore.currCurrency.min_free_delivery_amount;
									this.userStore.currBusiness.delivery_courier_free_shipping = this.lastFreeDeliveryValue ? this.currencyStore.actualToDbValue(this.lastFreeDeliveryValue) : minValue;
								} else {
									this.lastFreeDeliveryValue = this.freeDeliveryValue;
									this.userStore.currBusiness.delivery_courier_free_shipping = null;
								}
								this.editedSettings = true;
							},
							valueCallback: (v) => {
								if(v !== null && v > 0) {
									this.userStore.currBusiness.delivery_courier_free_shipping = this.currencyStore.actualToDbValue(v);
									this.lastFreeDeliveryValue = v;
									this.editedSettings = true;
								}
							}
						},
						{title: this.getString("pickup_delay"), titleNumber: this.getString("pickup_delay"), value: this.userStore.currBusiness && this.userStore.currBusiness.delivery_courier_pickup_delay ? this.userStore.currBusiness.delivery_courier_pickup_delay : 1, min: 1, customSlot: "number-input-slot", explain: this.getString("pickup_delay_explain"), valueCallback: (v) => {this.userStore.currBusiness.delivery_courier_pickup_delay = v; this.editedSettings = true;}},
					],
				});
			}

			let otherItems = [{
				title: this.getString('max_distance'),
				customSlot: 'number-input-slot',
				value: this.maxDistance || 0,
				symbol: 'm',
				rightSymbol: true,
				valueCallback: (newStatus) => {
					this.editedSettings = true;
					this.userStore.currBusiness.delivery_distance = newStatus;
				},
			}, {
				title: this.getString('min_distance'),
				customSlot: 'number-input-slot',
				value: this.minDistance || 0,
				symbol: 'm',
				rightSymbol: true,
				valueCallback: (newStatus) => {
					this.editedSettings = true;
					this.userStore.currBusiness.delivery_min_distance = newStatus;
				},
			}, {
				title: this.getString('additional_costs_currency', [this.currencyStore.currCurrency.symbol]),
				customSlot: 'amount-input-slot',
				value: this.deliveryCosts || 0,
				valueCallback: (newStatus) => {
					this.editedSettings = true;
					this.userStore.currBusiness.delivery_costs = this.currencyStore.actualToDbValue(newStatus);
				},
			}, {
				title: this.getString('min_order_currency', [this.currencyStore.currCurrency.symbol]),
				customSlot: 'amount-input-slot',
				value: this.deliveryMinOrder || 0,
				valueCallback: (newStatus) => {
					this.editedSettings = true;
					this.userStore.currBusiness.delivery_min_order = this.currencyStore.actualToDbValue(newStatus);
				},
			}, {
				title: this.getString('scheduled_delivery'),
				switch: true,
				active: this.deliveryFutureDays !== null && this.deliveryFutureDays !== 0,
				value: this.deliveryFutureDays || 0,
				dropdownSlot: 'future-days',
				valueCallback: (days) => {
					this.editedSettings = true;
					this.userStore.currBusiness.delivery_future_days = days;
				},
				callback: (newStatus) => {
					this.editedSettings = true;
					this.userStore.currBusiness.delivery_future_days = newStatus ? 1 : null;
				},
			}];

			return items.concat(otherItems);
		},
		deliveryCash() {
			return this.userStore.currBusiness ? this.userStore.currBusiness.delivery_cash : false;
		},
		otherPaymentsActive() {
			return this.userStore.currBusiness ? this.userStore.currBusiness.sub_otherpayments_id : false;
		},
		deliveryCourier() {
			return this.userStore.currBusiness ? this.userStore.currBusiness.delivery_courier : false;
		},
		maxDistance() {
			return this.userStore.currBusiness ? this.userStore.currBusiness.delivery_distance : null;
		},
		minDistance() {
			return this.userStore.currBusiness ? this.userStore.currBusiness.delivery_min_distance : null;
		},
		deliveryCosts() {
			return this.userStore.currBusiness ? this.userStore.currBusiness.delivery_costs : null;
		},
		deliveryMinOrder() {
			return this.userStore.currBusiness ? this.userStore.currBusiness.delivery_min_order : null;
		},
		deliveryFutureDays() {
			return this.userStore.currBusiness ? this.userStore.currBusiness.delivery_future_days : null;
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else if(this.windowWidth < 1440) {
				return "960px";
			} else {
				return "1280px";
			}
		}
	},
	methods: {
		togglingPopupSheet(v) {
			if(!v) {
				this.clientSecret = null;
			}
		},
		paymentComplete() {
			setTimeout(() => {
				this.userStore.refreshBusiness(this.userStore.currBusinessId);
			}, 2000);
			setTimeout(() => {
				this.clientSecret = null;
			}, 2500);
		},
		closeTimetablesSheet(open) {
			if(!open) {
				this.timetablesSheetActive = false;
				this.loadedTimetables = false;
			}
		},
		openTimetables() {
			this.timetablesSheetActive = true;
			this.loadingTimetables = true;
			this.loadedTimetables = false;
			this.userStore.getDeliveryTimetables(timetables => {
				this.loadingTimetables = false;
				this.loadedTimetables = true;
				this.timetables = timetables;
			});
		},
		changeDeliveryCash(newStatus) {
			this.editedSettings = true;
			this.userStore.currBusiness.delivery_cash = newStatus;
			requestAnimationFrame(() => {
				if (!this.otherPaymentsActive) {
					this.userStore.currBusiness.delivery_cash = false;
					this.$refs["popup-other-payments"].toggleStatus();
				}
			});
		},
		cancelSubOtherPayments() {
			this.userStore.currBusiness.delivery_cash = false;
			this.$refs["popup-other-payments"].toggleStatus();
		},
		subOtherPaymentsMethod() {
			this.loadingOtherPayments = true;
			this.userStore.subOtherPaymentsMethods("delivery-cash", (success, details) => {
				this.$refs["popup-other-payments"].toggleStatus();
				this.loadingOtherPayments = false;
				if(!success) {
					if(details && details === 'currency_mismatch') {
						this.showNotification("user-currency-mismatch", [this.userStore?.user?.name]);
					} else {
						this.showNotification("generic-fail");
					}
				} else {
					this.clientSecret = success;
				}
			});
		},
		saveSettings() {
			if(!this.editedSettings) {
				this.showNotification("generic-success");
			} else {
				const minDistance = this.userStore.currBusiness?.delivery_min_distance;
				const maxDistance = this.userStore.currBusiness?.delivery_distance;
				if(minDistance && maxDistance && minDistance > maxDistance) {
					this.showNotification("min-distance-too-high");
					return;
				}
				this.$emit('save-settings');
			}
		}
	},
	watch: {
		'userStore.currBusinessId': function (newVal, oldVal) {
			if (newVal !== null && newVal !== oldVal) {
				this.editedSettings = false;
				this.lastFreeDeliveryValue = null;
			}
		}
	}
}
</script>

<style lang="scss" scoped>
@use "@/console-new/assets/scss/_variables.scss" as *;

.card-margin {
	margin-top: $mainPadding;
}
.extra-text {
	width: 100%;
	padding-top: 10px;
	transition: opacity 0.3s, max-height .3s, padding-top .3s;
	max-height: 200px;
	p {
		font-size: 14px;
		color: #9B9B9B;
	}
	&.hide {
		opacity: 0;
		max-height: 0;
		padding-top: 0;
	}
}
.checklist-container {
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;
}
.number-p {
	width: calc(100% - 130px);
	@media (max-width: 768px) {
		width: calc(100% - 110px);
	}
}
.switch-p {
	width: calc(100% - 110px);
	@media (max-width: 768px) {
		width: calc(100% - 80px);
	}
}
.button-p {
	width: calc(100% - 120px);
}
.input-item {
	@media (max-width: 350px) {
		margin-left: auto;
	}
}
.switch-p, .number-p, .button-p {
	@media (max-width: 600px) {
		font-size: 14px;
	}
	@media (max-width: 400px) {
		font-size: 12px;
	}
	@media (max-width: 350px) {
		width: 100%;
		margin-bottom: 6px;
	}
}
</style>